// A studio stereo mix for a day's 20 sounds, like an engineer would set up a band on a stage:
// kick, snare and bass dead centre and dry (in front); hats and ride to one side, shaker and
// tambourine to the other; toms from low to high across the stereo field; hand percussion spread
// wide; chords and the classic instrument on either side of a near-centre lead, a little further
// back (more room). Volumes stay as calibrated; only pan and the room sends move. Used by the
// Wizard's example songs and by the editor's "Studio mix" button.
import type { PeriodSpec } from "./generated/PeriodSpec";
import type { SoundMix } from "./generated/SoundMix";
import { isAtmosPad, laneSlot, NUM_PADS } from "./period";
import { INSTRUMENT_ROLES } from "./presets";

export type MixGroup =
  | "kick"
  | "snare"
  | "clap"
  | "rim"
  | "ghost"
  | "hat"
  | "open"
  | "ride"
  | "crash"
  | "tom"
  | "shaker"
  | "perc"
  | "bass"
  | "harmony"
  | "lead"
  | "classic";

/** What a drum pad is, for mixing, from its name. */
export function mixGroupOf(name: string): MixGroup {
  const n = name.toLowerCase();
  if (/ghost/.test(n) && /snare/.test(n)) return "ghost";
  if (/kick|boom/.test(n)) return "kick";
  if (/clap/.test(n)) return "clap";
  if (/rim|stick/.test(n)) return "rim";
  if (/snare|snap/.test(n)) return "snare";
  if (/open/.test(n)) return "open";
  if (/hat|hh/.test(n)) return "hat";
  if (/ride/.test(n)) return "ride";
  if (/crash|cymbal/.test(n)) return "crash";
  if (/tom/.test(n)) return "tom";
  if (/shak|tamb/.test(n)) return "shaker";
  return "perc";
}

export interface StudioMixOptions {
  /** Mirror the stage (hats on the left instead of the right …). */
  mirror?: boolean;
  /** How wide the spread is (0.6 narrow … 1.2 wide; 1 = default). */
  width?: number;
}

const clamp = (x: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, x));
const r2 = (x: number) => Math.round(x * 100) / 100;

/**
 * The studio mix of a period's sounds: `mix` (the song's current mix, one entry per sound) with
 * pan and room sends set per sound; volume, cutoff and inserts are kept.
 */
export function studioMix(spec: PeriodSpec, mix: readonly SoundMix[], opts: StudioMixOptions = {}): SoundMix[] {
  const side = opts.mirror ? -1 : 1;
  const width = clamp(opts.width ?? 1, 0.4, 1.4);
  const groups = spec.sounds.map((s, i) =>
    i < NUM_PADS ? mixGroupOf(s.name) : isAtmosPad(i) ? ("atmos" as const) : INSTRUMENT_ROLES[i - NUM_PADS],
  );
  // Toms and percussion spread in their order on the kit (low first).
  const toms = groups.map((g, i) => (g === "tom" ? i : -1)).filter((i) => i >= 0);
  const percs = groups.map((g, i) => (g === "perc" ? i : -1)).filter((i) => i >= 0);
  const hats = groups.map((g, i) => (g === "hat" ? i : -1)).filter((i) => i >= 0);
  const placed = spec.sounds.map((s, i) => {
    const m = { ...(mix[i] ?? s.default_mix) };
    let pan = 0;
    let reverb = m.reverb_send;
    let delay = m.delay_send;
    switch (groups[i]) {
      case "kick":
      case "bass":
        pan = 0;
        reverb = 0;
        delay = 0;
        break;
      case "snare":
        pan = 0;
        reverb = clamp(reverb, 0.06, 0.16);
        break;
      case "clap":
        pan = 0;
        reverb = clamp(reverb, 0.1, 0.22);
        break;
      case "ghost":
        pan = 0.06;
        reverb = clamp(reverb, 0.05, 0.12);
        break;
      case "rim":
        pan = 0.14;
        reverb = clamp(reverb, 0.06, 0.16);
        break;
      case "hat": {
        const k = hats.indexOf(i);
        pan = 0.26 + 0.08 * k;
        reverb = Math.min(reverb, 0.05);
        break;
      }
      case "open":
        pan = 0.3;
        reverb = clamp(reverb, 0.04, 0.1);
        break;
      case "ride":
        pan = 0.42;
        reverb = clamp(reverb, 0.04, 0.1);
        break;
      case "crash":
        pan = -0.36;
        reverb = clamp(reverb, 0.06, 0.14);
        break;
      case "shaker":
        pan = -0.4;
        reverb = clamp(reverb, 0.04, 0.12);
        break;
      case "tom": {
        const k = toms.indexOf(i);
        pan = toms.length > 1 ? -0.4 + (0.8 * k) / (toms.length - 1) : 0;
        reverb = clamp(reverb, 0.08, 0.18);
        break;
      }
      case "perc": {
        // Alternate sides, wider for each further one; low drums stay nearer the centre.
        const k = percs.indexOf(i);
        const low = /low|bass|deep/i.test(s.name);
        pan = (k % 2 === 0 ? -1 : 1) * (low ? 0.25 : 0.4 + 0.06 * Math.floor(k / 2));
        reverb = clamp(reverb, 0.08, 0.2);
        break;
      }
      case "harmony":
        pan = -0.22;
        reverb = clamp(reverb + 0.05, 0.15, 0.45);
        break;
      case "lead":
        pan = 0.06;
        reverb = clamp(reverb, 0.1, 0.35);
        break;
      case "classic":
        pan = 0.3;
        reverb = clamp(reverb + 0.05, 0.15, 0.45);
        break;
      case "atmos":
        // The atmosphere wraps around everything, far back: pads keep their gentle spread.
        pan = m.pan;
        reverb = clamp(reverb + 0.05, 0.3, 0.6);
        break;
    }
    m.pan = r2(clamp(pan * side * width, -1, 1));
    m.reverb_send = r2(clamp(reverb, 0, 0.6));
    m.delay_send = r2(clamp(delay, 0, 0.5));
    return m;
  });
  if (mix.length <= spec.sounds.length) return placed;
  // Extra instrument lanes (32–39) sit opposite their instrument's main lane, so two harmonies or
  // two leads spread left and right; a second bass stays close to the centre and dry.
  const extra = mix.slice(spec.sounds.length).map((m0, k) => {
    const sound = spec.sounds.length + k;
    const main = placed[laneSlot(sound)];
    const m = { ...m0 };
    const alt = k % 2 === 0 ? -1 : 1;
    const bass = laneSlot(sound) === NUM_PADS;
    const pan = bass ? 0.1 * alt * side : Math.abs(main.pan) < 0.1 ? 0.24 * alt * side * width : -main.pan;
    m.pan = r2(clamp(pan, -1, 1));
    m.reverb_send = bass ? 0 : main.reverb_send;
    m.delay_send = bass ? 0 : r2(clamp(m.delay_send, 0, 0.5));
    return m;
  });
  return [...placed, ...extra];
}
